import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, IconButton, CircularProgress, Alert,
  Card, CardContent, InputAdornment, Tooltip, useMediaQuery, useTheme,
  TablePagination, Stack
} from '@mui/material';
import {
  Add as AddIcon, Search as SearchIcon, Visibility as ViewIcon,
  Refresh as RefreshIcon, ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SettlementService from '../../services/settlement.service';
import { getBrandedTableHeadSx } from '../../utils/colorUtils';
import { can } from '../../utils/roleUtils';
import { ProTableStats } from '../../components/pro-table';

const fontSize = {
  xs: '0.75rem',
  sm: '0.8125rem',
  md: '0.875rem',
};

const Settlements = ({ currentUser }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const typingCenterId = currentUser?.typingCenterId;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [filters, setFilters] = useState({ search: '' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  useEffect(() => {
    if (!typingCenterId) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, typingCenterId]);

  const fetchData = async () => {
    if (!typingCenterId) return;
    setLoading(true);
    try {
      const [settResult, statsResult] = await Promise.all([
        SettlementService.getAll({ ...filters, typingCenterId }),
        SettlementService.getStatistics(typingCenterId)
      ]);
      setSettlements(settResult.data);
      setStatistics(statsResult);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load settlements');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => date ? new Date(date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';

  const paginatedSettlements = settlements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const renderSummaryCards = () => (
    <ProTableStats
      loading={!statistics}
      cards={[
        { label: 'Settlements (MTD)', value: statistics?.settlementsThisMonth ?? 0 },
        { label: 'Total Settled', value: SettlementService.formatCurrency(statistics?.totalSettled || 0), color: 'success.main' },
        { label: 'Average Settlement', value: SettlementService.formatCurrency(statistics?.averageSettlement || 0), color: 'warning.main' },
        { label: 'Invoices Settled', value: statistics?.invoicesSettled ?? 0, color: 'info.main' },
      ]}
    />
  );

  const getModeChip = (mode) => {
    if (mode === 'AUTO') return <Chip label="Auto" size="small" color="info" sx={{ fontSize: fontSize.xs, height: 22 }} />;
    return <Chip label="Custom" size="small" color="secondary" sx={{ fontSize: fontSize.xs, height: 22 }} />;
  };

  return (
    <Box sx={{ pb: isMobile ? 2 : 0, width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {renderSummaryCards()}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1.5 }}>
        <TextField size="small" placeholder="Search by settlement # or customer..."
          value={filters.search} onChange={(e) => { setFilters(prev => ({ ...prev, search: e.target.value })); setPage(0); }}
          sx={{ flex: '1 1 200px', minWidth: 200, '& .MuiInputBase-root': { height: 32 }, '& .MuiInputBase-input': { fontSize: fontSize.sm } }}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: 'text.disabled' }} /></InputAdornment> }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto', flexShrink: 0, alignItems: 'center' }}>
          <Typography sx={{ fontSize: fontSize.xs, color: 'text.secondary', whiteSpace: 'nowrap', mr: 1 }}>
            {settlements.length} settlements
          </Typography>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={fetchData} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {can(currentUser, 'finance', 'write') && (
            <Button variant="contained" size="small" startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate('/settlements/create')}
              sx={{ fontSize: fontSize.sm, textTransform: 'none', ml: 0.5 }}>
              Create Settlement
            </Button>
          )}
        </Box>
      </Box>

      {isMobile ? (
        <Stack spacing={1.5}>
          {loading ? <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box> :
          paginatedSettlements.length === 0 ? <Paper sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No settlements found</Typography></Paper> :
          paginatedSettlements.map((s) => (
            <Card key={s.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/settlements/${s.id}`)}>
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Box>
                    <Typography sx={{ fontSize: fontSize.sm, fontWeight: 600, color: 'secondary.main' }}>{s.settlementNumber}</Typography>
                    <Typography sx={{ fontSize: fontSize.xs }}>{s.customerName}</Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {getModeChip(s.mode)}
                    <ChevronRightIcon color="action" />
                  </Box>
                </Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: fontSize.xs }}>{formatDate(s.date)} - {s.invoices?.length} invoices</Typography>
                  <Typography sx={{ fontSize: fontSize.sm, fontWeight: 700, color: isDark ? '#4caf50' : '#2e7d32' }}>{SettlementService.formatCurrency(s.totalAmount)}</Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead sx={getBrandedTableHeadSx(theme)}>
                <TableRow>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Settlement #</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Date</TableCell>
                  <TableCell sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Customer</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Mode</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Invoices</TableCell>
                  <TableCell align="right" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Total Settled</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Status</TableCell>
                  <TableCell align="center" sx={{ fontSize: fontSize.xs, fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={28} /></TableCell></TableRow>
                ) : paginatedSettlements.length === 0 ? (
                  <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><Typography color="text.secondary" sx={{ fontSize: fontSize.sm }}>No settlements found</Typography></TableCell></TableRow>
                ) : paginatedSettlements.map((s) => (
                  <TableRow key={s.id} hover>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: 'secondary.main', cursor: 'pointer' }}
                        onClick={() => navigate(`/settlements/${s.id}`)}>
                        {s.settlementNumber}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '13px', py: 1 }}>{formatDate(s.date)}</TableCell>
                    <TableCell sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>{s.customerName || '—'}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>{getModeChip(s.mode)}</TableCell>
                    <TableCell align="center" sx={{ fontSize: '13px', py: 1 }}>{s.invoiceCount ?? (s.invoices?.length || 0)}</TableCell>
                    <TableCell align="right" sx={{ py: 1 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 600, color: isDark ? '#4caf50' : '#2e7d32' }}>{SettlementService.formatCurrency(s.totalAmount)}</Typography>
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Chip
                        label={s.status === 'CANCELLED' ? 'Cancelled' : 'Completed'}
                        size="small"
                        color={s.status === 'CANCELLED' ? 'error' : 'success'}
                        sx={{ fontSize: fontSize.xs, height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ py: 1 }}>
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => navigate(`/settlements/${s.id}`)}>
                          <ViewIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination component="div" count={settlements.length} page={page}
            onPageChange={(e, p) => setPage(p)} rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </Paper>
      )}
    </Box>
  );
};

export default Settlements;
